import React, { useState } from "react";
import { toast } from "sonner";
import { Mail } from "lucide-react";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import Breadcrumbs from "@/components/Breadcrumbs";
import { breadcrumbLd } from "@/lib/siteContent";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";

export default function Contact() {
  const [f, setF] = useState({ name: "", phone: "", email: "", subject: "", message: "", complaint_id: "" });
  const [consent, setConsent] = useState(false);
  const [captcha, setCaptcha] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async () => {
    if (!f.name || !f.subject || !f.message) return toast.error("Please fill name, subject and message");
    if (!captcha) return toast.error("Please confirm you are not a robot");
    if (!consent) return toast.error("Please accept the consent checkbox");
    try {
      await api.post("/contact", { ...f, consent });
      toast.success("Thank you. We have received your message.");
      setF({ name: "", phone: "", email: "", subject: "", message: "", complaint_id: "" });
      setConsent(false); setCaptcha(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send message");
    }
  };

  return (
    <div className="ca-container py-12 grid lg:grid-cols-3 gap-8">
      <Seo title="Contact ComplaintAdvisor | Support and General Enquiries"
        description="Contact ComplaintAdvisor for platform support, account questions, vendor enquiries, correction requests, privacy requests, and general information."
        path="/contact" jsonld={breadcrumbLd([{ label: "Home", to: "/" }, { label: "Contact Us", to: "/contact" }])} />
      <div className="lg:col-span-2">
        <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Contact Us", to: "/contact" }]} />
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 mt-3">Contact ComplaintAdvisor</h1>
        <p className="text-slate-600 mt-2">Send us a message and we will get back to you.</p>
        <div className="mt-6 grid sm:grid-cols-2 gap-4">
          <div><Label>Name</Label><Input data-testid="contact-name" value={f.name} onChange={set("name")} /></div>
          <div><Label>Mobile number</Label><Input data-testid="contact-phone" value={f.phone} onChange={set("phone")} /></div>
          <div><Label>Email</Label><Input data-testid="contact-email" value={f.email} onChange={set("email")} /></div>
          <div><Label>Complaint ID (optional)</Label><Input data-testid="contact-complaint-id" value={f.complaint_id} onChange={set("complaint_id")} /></div>
          <div className="sm:col-span-2"><Label>Subject</Label><Input data-testid="contact-subject" value={f.subject} onChange={set("subject")} /></div>
          <div className="sm:col-span-2"><Label>Message</Label><Textarea data-testid="contact-message" rows={5} value={f.message} onChange={set("message")} /></div>
        </div>
        <div className="mt-3 space-y-2">
          <label className="flex items-center gap-2 text-xs text-slate-600"><Checkbox checked={captcha} onCheckedChange={setCaptcha} data-testid="contact-captcha" /> I'm not a robot</label>
          <label className="flex items-start gap-2 text-xs text-slate-600"><Checkbox checked={consent} onCheckedChange={setConsent} data-testid="contact-consent" className="mt-0.5" /> I consent to ComplaintAdvisor contacting me regarding this enquiry.</label>
        </div>
        <Button className="mt-4 bg-teal-brand hover:bg-teal-700 text-white" onClick={submit} data-testid="contact-submit">Send Message</Button>
      </div>
      <aside className="space-y-3">
        <h3 className="font-display font-bold text-slate-900">Reach us</h3>
        {[["support@complaintadvisor.com", "Support & account help"], ["enquiry@complaintadvisor.com", "General enquiries"], ["business@complaintadvisor.com", "Vendor & business"]].map(([e, d]) => (
          <div key={e} className="rounded-xl border border-slate-200 bg-white p-4">
            <p className="text-sm font-medium text-slate-800 flex items-center gap-2"><Mail className="h-4 w-4 text-teal" /> {e}</p>
            <p className="text-xs text-slate-500 mt-1">{d}</p>
          </div>
        ))}
      </aside>
    </div>
  );
}
